import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useLocale } from "@/i18n/context";
import { companyRows, type CompanyLabels } from "@/config/company";
import { openCookieSettings } from "@/lib/consent";

export type LegalBlock =
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  /** Highlighted callout for the clauses a reader must not miss. */
  | { type: "note"; text: string }
  /** Provider identification, expanded from src/config/company.ts. */
  | { type: "company" }
  | { type: "table"; head: string[]; rows: string[][] }
  | { type: "links"; items: Array<{ label: string; href: string }> }
  /** Button that reopens the cookie preference dialog. */
  | { type: "cookieSettings"; label: string };

export type LegalSubsection = {
  title: string;
  blocks: LegalBlock[];
};

export type LegalSection = {
  title: string;
  blocks?: LegalBlock[];
  subsections?: LegalSubsection[];
};

export type LegalDocument = {
  eyebrow: string;
  title: string;
  /** Rendered under the title, e.g. "Zadnja posodobitev: 1. avgust 2026". */
  updated?: string;
  /** Short lead paragraph above the first section. */
  intro?: string;
  sections: LegalSection[];
};

/** Locale-specific chrome the blocks need but that isn't part of a document. */
export type LegalCommon = {
  company: CompanyLabels;
  tocHeading: string;
};

function CompanyBlock({ labels }: { labels: CompanyLabels }) {
  return (
    <dl className="mb-4 grid gap-x-6 gap-y-2 rounded-xl border border-border/60 bg-muted/30 p-4 text-base sm:grid-cols-[minmax(11rem,auto)_1fr] sm:p-5">
      {companyRows(labels).map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="font-medium text-foreground/80">{label}</dt>
          <dd className="m-0 mb-2 sm:mb-0">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function TableBlock({ head, rows }: { head: string[]; rows: string[][] }) {
  return (
    // Wide tables (subprocessors, retention) must scroll inside their own box
    // rather than pushing the page into horizontal scroll on a phone.
    <div className="mb-4 -mx-2 overflow-x-auto sm:mx-0">
      <table className="w-full min-w-[34rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            {head.map((cell) => (
              <th
                key={cell}
                scope="col"
                className="px-3 py-2 text-left font-semibold text-foreground/80"
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-border/50 align-top">
              {row.map((cell, j) => (
                <td key={j} className="px-3 py-2">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderBlock(block: LegalBlock, key: number, common: LegalCommon) {
  switch (block.type) {
    case "p":
      return (
        <p key={key} className="mb-4">
          {block.text}
        </p>
      );
    case "ul":
      return (
        <ul key={key} className="list-disc pl-6 mb-4">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={key} className="list-decimal pl-6 mb-4">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      );
    case "note":
      return (
        <p
          key={key}
          className="mb-4 rounded-xl border-l-4 border-primary/60 bg-primary/5 px-4 py-3 text-base"
        >
          {block.text}
        </p>
      );
    case "company":
      return <CompanyBlock key={key} labels={common.company} />;
    case "table":
      return <TableBlock key={key} head={block.head} rows={block.rows} />;
    case "links":
      return (
        <ul key={key} className="list-disc pl-6 mb-4">
          {block.items.map((item) => (
            <li key={item.href}>
              <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      );
    case "cookieSettings":
      return (
        <p key={key} className="mb-4">
          <button
            type="button"
            onClick={openCookieSettings}
            className="rounded-lg border border-primary/40 bg-primary/5 px-4 py-2 text-base font-medium text-primary transition-colors hover:bg-primary/10"
          >
            {block.label}
          </button>
        </p>
      );
  }
}

/** Stable slug so the table of contents can link into a section. */
const sectionId = (title: string, index: number) =>
  `s-${index + 1}-${title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 40)}`;

function renderSection(section: LegalSection, index: number, common: LegalCommon) {
  return (
    <section key={section.title} id={sectionId(section.title, index)} className="mb-8 scroll-mt-24">
      <h2 className="text-2xl font-semibold mb-4">{section.title}</h2>
      {section.blocks?.map((block, i) => renderBlock(block, i, common))}
      {section.subsections?.map((sub) => (
        <div key={sub.title}>
          <h3 className="text-xl font-semibold mb-3">{sub.title}</h3>
          {sub.blocks.map((block, i) => renderBlock(block, i, common))}
        </div>
      ))}
    </section>
  );
}

export function LegalPageLayout({ document }: { document: LegalDocument }) {
  const { t } = useLocale();
  const common = t.legal.common as LegalCommon;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="pt-16 bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <p className="section-eyebrow mb-4">{document.eyebrow}</p>
          <h1 className="section-title text-left mb-3">{document.title}</h1>
          {document.updated && (
            <p className="mb-8 text-sm text-muted-foreground">{document.updated}</p>
          )}
          <div className="surface-card border-0 bg-gradient-card p-6 sm:p-10 prose prose-lg max-w-none prose-headings:tracking-tight">
            {document.intro && <p className="lead mb-8">{document.intro}</p>}

            <nav aria-label={common.tocHeading} className="mb-10 not-prose">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-foreground/70">
                {common.tocHeading}
              </p>
              <ol className="grid gap-1.5 text-sm sm:grid-cols-2">
                {document.sections.map((section, i) => (
                  <li key={section.title}>
                    <a
                      href={`#${sectionId(section.title, i)}`}
                      className="text-muted-foreground transition-colors hover:text-primary"
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {document.sections.map((section, i) => renderSection(section, i, common))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function useLegalDocument(
  key: "privacy" | "terms" | "cookies"
): LegalDocument {
  const { t } = useLocale();
  // The i18n Translations type widens value literals to `string`, which drops
  // the LegalBlock `type` discriminant. The data is structurally a
  // LegalDocument at runtime, so re-assert the discriminated shape here.
  return t.legal[key] as LegalDocument;
}
